import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import EntitySelector from '@/components/common/EntitySelector';
import { calendarAPI, userAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

// Two-step: (1) pick users, (2) confirmation preview counts, then commit.
export default function BulkInviteDialog({ open, event, onClose, onDone, defaultRegisterDirectly = false }) {
  const [selection, setSelection] = useState([]);
  const [notes, setNotes] = useState('');
  // When on, inserts rows as accepted+registered instead of invited.
  // Use case: the event manager already has verbal/phone confirmation
  // and just wants to book the seat without triggering the invite loop.
  const [registerDirectly, setRegisterDirectly] = useState(defaultRegisterDirectly);
  const [step, setStep] = useState('pick'); // 'pick' | 'confirm' | 'busy' | 'result'
  const [result, setResult] = useState(null);

  // Sync when the parent opens the dialog with a different default
  // (e.g. the "Register Agent" shortcut vs. the "Bulk Invite" button).
  React.useEffect(() => {
    if (open) setRegisterDirectly(defaultRegisterDirectly);
  }, [open, defaultRegisterDirectly]);

  const fetchPage = useCallback(async ({ search, page, pageSize }) => {
    const res = await userAPI.listPaginated({ search, page, page_size: pageSize, status: 'active' });
    const items = (res.data.items || []).map((u) => ({
      value: u.user_id,
      label: u.name || u.email,
      sublabel: u.email,
    }));
    return { items, has_more: res.data.has_more, total: res.data.total };
  }, []);

  // Preview counts (client-side, since we already have the selection).
  const previewCounts = useMemo(() => {
    const capacity = event?.capacity;
    const seats = event?.assigned_participant_count_denorm ?? event?.assigned_participant_count ?? 0;
    const available = capacity != null ? Math.max(0, capacity - seats) : null;
    return {
      audience_found: selection.length,
      already_invited: 0, // resolved by the backend at commit time; shown as "computed on submit"
      new_invitations: selection.length,
      skipped_users: 0,
      remaining_capacity: available,
    };
  }, [selection, event]);

  const reset = () => {
    setSelection([]);
    setNotes('');
    setRegisterDirectly(defaultRegisterDirectly);
    setStep('pick');
    setResult(null);
  };

  const handleClose = () => {
    if (step === 'busy') return; // don't close mid-request
    onClose?.();
    setTimeout(reset, 200); // reset after close animation
  };

  const commit = async () => {
    if (selection.length === 0) return;
    setStep('busy');
    try {
      const res = await calendarAPI.inviteParticipants(
        event.event_id,
        selection.map((s) => s.value),
        notes.trim() || null,
        { register_directly: registerDirectly },
      );
      setResult(res.data);
      setStep('result');
      onDone?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Invite failed'));
      setStep('confirm');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-invite-dialog">
        <DialogHeader>
          <DialogTitle>{registerDirectly ? 'Register Participants' : 'Bulk Invite Participants'}</DialogTitle>
          <DialogDescription>
            {registerDirectly ? (
              <>Select users to <strong>register directly</strong> for <strong>{event?.title}</strong>.
              Each row is inserted as <em>accepted &amp; registered</em> — use this only when the
              user has already confirmed they will attend.</>
            ) : (
              <>Select users to invite to <strong>{event?.title}</strong>. Each user will receive an
              <em> invited</em> assignment and count against event capacity.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Search users</Label>
              <div className="mt-1">
                <EntitySelector
                  fetchPage={fetchPage}
                  value={selection}
                  onChange={setSelection}
                  placeholder="Search by name or email…"
                  testId="bulk-invite-users"
                />
              </div>
            </div>
            <label
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 cursor-pointer"
              data-testid="bulk-invite-register-directly-row"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={registerDirectly}
                onChange={(e) => setRegisterDirectly(e.target.checked)}
                data-testid="bulk-invite-register-directly-checkbox"
              />
              <div className="text-xs">
                <div className="font-medium text-slate-800">Register directly (skip invite)</div>
                <div className="text-slate-500">
                  Insert selected users as <em>accepted &amp; registered</em> — use when the
                  agent has already confirmed they will attend.
                </div>
              </div>
            </label>
            <div>
              <Label className="text-xs text-slate-600">Notes (KR-internal, optional)</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Not visible to invitees"
                data-testid="bulk-invite-notes"
              />
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-3" data-testid="bulk-invite-preview">
            <p className="text-sm text-slate-600">
              You are about to {registerDirectly ? <strong>register</strong> : 'invite'} <strong>{selection.length}</strong> user{selection.length === 1 ? '' : 's'}
              {registerDirectly
                ? '. Each row will be marked as accepted + registered. Existing invitations for the same users will be upgraded.'
                : '. The server will finalise the counts below at commit time — users already invited will be skipped, and inserts stop at capacity.'}
            </p>
            <CountsGrid counts={previewCounts} />
            <details className="text-xs">
              <summary className="text-slate-600 cursor-pointer">Selected users ({selection.length})</summary>
              <ul className="mt-1 max-h-32 overflow-y-auto space-y-0.5 pl-4 list-disc" data-testid="bulk-invite-selection-list">
                {selection.map((s) => (
                  <li key={s.value} className="text-slate-700 truncate">
                    {s.label} <span className="text-slate-400">({s.sublabel || s.value})</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {step === 'busy' && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="bulk-invite-busy">Inviting…</div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3" data-testid="bulk-invite-result">
            <p className="text-sm text-slate-700">Invitation processed.</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <ResultCell label="Created" value={result.invited?.length ?? 0} tone="emerald" />
              <ResultCell label="Skipped (already active)" value={result.skipped_existing?.length ?? 0} tone="slate" />
              <ResultCell label="Capacity reached" value={result.capacity_reached ? 'Yes' : 'No'} tone={result.capacity_reached ? 'amber' : 'slate'} />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'pick' && (
            <>
              <Button variant="ghost" onClick={handleClose} data-testid="bulk-invite-cancel">Cancel</Button>
              <Button
                disabled={selection.length === 0}
                onClick={() => setStep('confirm')}
                data-testid="bulk-invite-next"
              >
                Next: preview
              </Button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="ghost" onClick={() => setStep('pick')} data-testid="bulk-invite-back">Back</Button>
              <Button onClick={commit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-invite-confirm">
                {registerDirectly ? `Confirm & Register ${selection.length}` : `Confirm & Invite ${selection.length}`}
              </Button>
            </>
          )}
          {step === 'busy' && <Button disabled>Please wait…</Button>}
          {step === 'result' && (
            <Button onClick={handleClose} data-testid="bulk-invite-close-result">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CountsGrid = ({ counts }) => (
  <div className="grid grid-cols-2 gap-2 text-xs" data-testid="counts-grid">
    <Cell label="Audience found" value={counts.audience_found} testid="counts-audience-found" />
    <Cell label="Already invited" value={counts.already_invited ?? 'computed on submit'} testid="counts-already-invited" />
    <Cell label="New invitations" value={counts.new_invitations} testid="counts-new-invitations" />
    <Cell label="Skipped users" value={counts.skipped_users ?? 'computed on submit'} testid="counts-skipped" />
    <Cell label="Remaining capacity" value={counts.remaining_capacity == null ? 'no cap' : counts.remaining_capacity} testid="counts-remaining-capacity" />
  </div>
);

const Cell = ({ label, value, testid }) => (
  <div className="rounded border border-slate-200 bg-slate-50 p-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value}</div>
  </div>
);

const ResultCell = ({ label, value, tone }) => {
  const cls = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone];
  return (
    <div className={`rounded border p-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
};
